// Implements: F-UIX-009, F-UIX-007, F-UIX-016
/**
 * The 48px topbar of the redesign mockup: the sidebar toggle, the breadcrumb of the current page and
 * a slot for the page's own actions (`PageActions` portals into it). Nothing global lives here
 * beyond navigation: no account controls, no theme (they are in the sidebar foot).
 */

import { createContext, useContext, useState } from "react";
import type { Dispatch, ReactElement, ReactNode, SetStateAction } from "react";
import { createPortal } from "react-dom";

import { useTranslation } from "../../shared/i18n";
import { Button } from "../../shared/ui/atoms/Button";
import { Icon } from "../../shared/ui/atoms/Icon";
import styles from "./Topbar.module.css";

type SlotState = [HTMLElement | null, Dispatch<SetStateAction<HTMLElement | null>>];

const ActionsSlot = createContext<SlotState | null>(null);
const TitleSlot = createContext<SlotState | null>(null);

/** Holds the elements the topbar exposes for a page's title and actions. Mounted once by the shell. */
export function TopbarSlotProvider({ children }: { readonly children: ReactNode }): ReactElement {
  const actions = useState<HTMLElement | null>(null);
  const title = useState<HTMLElement | null>(null);
  return (
    <ActionsSlot value={actions}>
      <TitleSlot value={title}>{children}</TitleSlot>
    </ActionsSlot>
  );
}

/**
 * A page's actions, drawn at the right of the topbar (e.g. the mockup's "Nuevo" or "Exportar CSV").
 * A page mounted with no shell around it (a test, an embed) draws them where it is instead.
 */
export function PageActions({ children }: { readonly children: ReactNode }): ReactElement | null {
  const slot = useContext(ActionsSlot);
  if (slot === null) return <>{children}</>;
  const element = slot[0];
  return element === null ? null : createPortal(children, element);
}

export interface TopbarProps {
  /** `drawer` below 1024px: the toggle opens the navigation sheet instead of folding the sidebar. */
  readonly mode: "sidebar" | "drawer";
  readonly onToggle: () => void;
  /** Breadcrumb from the outermost to the current page; the last one is the current page. */
  readonly crumbs: readonly string[];
  /** Global controls at the far right (the palette's search button). */
  readonly trailing?: ReactNode;
}

export function Topbar({ mode, onToggle, crumbs, trailing }: TopbarProps): ReactElement {
  const { t } = useTranslation();
  const slot = useContext(ActionsSlot);
  const setSlot = slot?.[1];
  const setTitle = useContext(TitleSlot)?.[1];
  return (
    <header className={styles.topbar}>
      <Button
        variant="ghost"
        iconOnly
        label={mode === "drawer" ? t("app.shell.openNavigation") : t("app.shell.toggleSidebar")}
        keyShortcuts={mode === "drawer" ? undefined : "["}
        onClick={onToggle}
      >
        <Icon name={mode === "drawer" ? "menu" : "panel-left"} />
      </Button>
      {crumbs.length === 0 ? null : (
        <nav className={styles.crumbs} aria-label={t("app.shell.breadcrumb")}>
          {crumbs.map((crumb, index) =>
            index === crumbs.length - 1 ? (
              <span key={crumb} className={styles.current} aria-current="page">
                {crumb}
              </span>
            ) : (
              <span key={crumb} className={styles.parent}>
                {crumb}
                <span className={styles.separator} aria-hidden="true" />
              </span>
            ),
          )}
        </nav>
      )}
      <div className={styles.title} ref={setTitle} />
      <span className={styles.spacer} />
      {trailing}
      <div className={styles.actions} ref={setSlot} />
    </header>
  );
}

/**
 * A page's own title area, drawn after the breadcrumb (the Lienzo editor's "Lienzo / <name>" and
 * its chips). Like `PageActions`, a page mounted with no shell draws it where it is.
 */
export function PageTitle({ children }: { readonly children: ReactNode }): ReactElement | null {
  const slot = useContext(TitleSlot);
  if (slot === null) return <>{children}</>;
  const element = slot[0];
  return element === null ? null : createPortal(children, element);
}
